import * as Tooltip from "@radix-ui/react-tooltip";
import classNames from "classnames/bind";
import React, { type ElementType } from "react";
import { IconLoading } from "../../../Icon";
import { Popover } from "../../../Popover";
import { Skeleton } from "../../../Skeleton";
import styles from "./DataDisplayWithAdornment.module.scss";
import type { DataDisplayWithAdornmentProps } from "./types";

const cx = classNames.bind(styles);

const DataDisplayWithAdornment = ({
  children,
  showAdornment = false,
  adornmentSlot,
  detailsTriggerProps,
  detailsSlot,
  detailsSlotDisplayMode = "popover",
  detailsSlotWrapperClassName,
  alignment = "left",
  isInline = false,
  isBusy = false,
  isLoading = false,
  className,
  style,
  as: asProp,
  ...restProps
}: DataDisplayWithAdornmentProps) => {
  const CustomNode = asProp ?? "div";

  return (
    <Skeleton
      isBusy={isBusy}
      isLoading={isLoading}
    >
      <CustomNode
        className={cx(
          "container",
          `align-${alignment}`,
          showAdornment && "container--with-type",
          isInline ? "container--inline" : "container--block",
          className
        )}
        style={style}
        {...restProps}
      >
        {showAdornment && alignment === "right" ? (
          <AdornmentWithDetails
            adornmentSlot={adornmentSlot}
            detailsTriggerProps={detailsTriggerProps}
            detailsSlot={detailsSlot}
            detailsSlotDisplayMode={detailsSlotDisplayMode}
            detailsSlotWrapperClassName={detailsSlotWrapperClassName}
          />
        ) : null}
        {children}
        {showAdornment &&
        (alignment === "left" || alignment === "justified") ? (
          <AdornmentWithDetails
            adornmentSlot={adornmentSlot}
            detailsTriggerProps={detailsTriggerProps}
            detailsSlot={detailsSlot}
            detailsSlotDisplayMode={detailsSlotDisplayMode}
            detailsSlotWrapperClassName={detailsSlotWrapperClassName}
          />
        ) : null}
      </CustomNode>
    </Skeleton>
  );
};

const AdornmentWithDetails = ({
  adornmentSlot,
  detailsTriggerProps,
  detailsSlot,
  detailsSlotDisplayMode,
  detailsSlotWrapperClassName,
  isBusy,
}: Pick<
  DataDisplayWithAdornmentProps,
  | "adornmentSlot"
  | "detailsTriggerProps"
  | "detailsSlot"
  | "detailsSlotDisplayMode"
  | "detailsSlotWrapperClassName"
  | "isBusy"
> & { as?: ElementType }) => {
  const content = isBusy ? (
    <IconLoading
      speed="fast"
      size={16}
    />
  ) : (
    adornmentSlot({
      iconProps: {
        size: 14,
      },
      className: cx("flair"),
    })
  );

  if (!detailsSlot) {
    return <>{content}</>;
  }

  return detailsSlotDisplayMode === "popover" ? (
    <Popover
      contentRender={() => detailsSlot}
      contentClassName={cx("popover-content", detailsSlotWrapperClassName)}
    >
      <button
        {...detailsTriggerProps}
        className={cx(
          "details-trigger-container",
          detailsTriggerProps?.className
        )}
        type="button"
      >
        {content}
      </button>
    </Popover>
  ) : (
    <Tooltip.Provider>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            {...detailsTriggerProps}
            type="button"
            className={cx(
              "details-trigger-container",
              detailsTriggerProps?.className
            )}
            onClick={(evt) => {
              evt.stopPropagation();
            }}
            onFocusCapture={(e) => e.stopPropagation()}
          >
            {content}
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className={cx("tooltip-content", detailsSlotWrapperClassName)}
            sideOffset={5}
            collisionPadding={16}
          >
            {detailsSlot}
            <Tooltip.Arrow className={cx("tooltip-arrow")} />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default DataDisplayWithAdornment;
